// Définitions d'armes et de profils de châssis, pilotées par données.
// Les longueurs/largeurs/décalages sont en multiples du rayon du tank ; les angles en degrés.
// Chaque valeur est une proposition initiale, à mesurer (voir docs/decisions.md).

/** Comportement de projectile (dossier §11 : drones, pièges, mines, obus, missiles, rayons, contrôle). */
export interface ProjectileBehavior {
  kind?: 'bullet' | 'drone' | 'trap' | 'mine' | 'shell' | 'missile' | 'beam' | 'orb';
  /** Décélération exponentielle (/s) : pièges posés. */
  drag?: number;
  /** Rebonds sur les bords de l'arène et perte de dégâts par rebond. */
  bounces?: number;
  bounceLoss?: number;
  /** Guidage vers le point visé (rad/s), après un délai éventuel. */
  homing?: number;
  homingDelay?: number;
  /** Accélération relative (/s) jusqu'à un plafond de vitesse. */
  accel?: number;
  maxSpeedMul?: number;
  /** Explosion : rayon (u) et multiplicateur de dégâts ; à l'impact et/ou à expiration. */
  explode?: { radius: number; damage: number; onHit?: boolean; onExpire?: boolean };
  /** Fragments non récursifs : un fragment ne redéclenche jamais l'effet parent. */
  frags?: { count: number; spread: number; damage: number; speed: number; life: number; onHit?: boolean; onExpire?: boolean };
  /** Tir en cloche vers le point visé, sans collision en vol, explosion à l'arrivée. */
  lob?: { minRange: number; maxRange: number };
  /** Mine : armée après ce délai, puis déclenchée dans ce rayon. */
  armDelay?: number;
  trigger?: number;
  /** Nombre maximum d'objets actifs pour ce tube (drones, pièges, mines). */
  maxActive?: number;
  /** Drones : rayon de contrôle autour du tank. */
  control?: number;
  /** Effets de contrôle à l'impact, plafonnés côté serveur. */
  slow?: { mul: number; dur: number };
  knock?: number;
  pull?: { radius: number; force: number };
  /** Chaîne d'énergie vers les cibles proches. */
  chain?: { jumps: number; range: number; falloff: number };
  /** Multiplicateur de dégâts contre les cibles blindées (formes lourdes, monstres, blindage directionnel). */
  vsArmor?: number;
  /** Piège tourelle : tire une seule fois sur un ennemi à portée (cône optionnel, degrés). */
  turret?: { range: number; damage: number; cone?: number };
  /** Mine magnétique : se rapproche d'un ennemi dans ce rayon. */
  seek?: number;
  /** Boomerang : revient vers le tank après ce délai (s). */
  returnAfter?: number;
  /** Drones : régénèrent leur pénétration (fraction/s) en orbite près du tank. */
  regen?: number;
  /** Rayon qui « chauffe » une cible suivie en continu : bonus par impact consécutif, plafonné. */
  stack?: { per: number; max: number };
  /** Drones d'escorte (module) : attaquent seuls la cible la plus proche dans ce rayon autour du tank, sinon orbitent. */
  escort?: number;
}

export interface BarrelDef {
  /** Angle relatif à la visée (degrés). */
  angle: number;
  /** Décalage latéral (rayons). */
  offset: number;
  length: number;
  width: number;
  /** Phase 0..1 dans le cycle de rechargement : sert aux tirs alternés. */
  delay: number;
  damage?: number;
  speed?: number;
  pen?: number;
  /** Multiplicateur de durée de vie du projectile (portée). */
  life?: number;
  /** Rayon du projectile en fraction de la largeur du tube. */
  size?: number;
  /** Dispersion aléatoire (degrés, demi-angle). */
  spread?: number;
  recoil?: number;
  /** Nombre de projectiles par tir (gerbes). */
  pellets?: number;
  /** Ouverture visuelle du tube à l'extrémité (gerbes), en rayons. */
  flare?: number;
  proj?: ProjectileBehavior;
  /** Tube secondaire automatique : vise seul la cible la plus proche et tire sans gâchette. */
  auto?: boolean;
  /** Forme visuelle du tube. */
  look?: 'tube' | 'launcher' | 'hatch' | 'emitter' | 'mortar' | 'trap' | 'mine';
  /** Tube ajouté par un module (tourelle, drones d'escorte) : cadence propre, dessiné par le module. */
  module?: 'turret' | 'drones';
}

export interface WeaponDef {
  /** Secondes entre deux cycles complets. */
  reload: number;
  barrels: BarrelDef[];
  burst?: { count: number; interval: number };
  /** Cadence croissante pendant le tir maintenu. */
  ramp?: { max: number; perShot: number; decay: number };
  /** Chauffe : 1 = surchauffe, plus de tir jusqu'à redescendre sous lockout. */
  heat?: { perShot: number; cool: number; lockout: number; cap?: number; resume?: number };
  /** Rotation continue de l'ensemble des tubes (degrés/s). */
  spin?: number;
  /** Tir chargé : maintien = charge, relâchement = tir. */
  charge?: { time: number; minFrac: number; damageAt0: number; damageAt1: number; moveMul: number };
  /** Resserrement de la dispersion pendant le maintien (0..1 = fraction restante au maximum). */
  focus?: { time: number; minSpread: number };
  /** Vitesse de rotation maximale de la tourelle (rad/s), sinon instantanée. */
  turnRate?: number;
  /** Multiplicateur de vitesse pendant que la gâchette est maintenue. */
  moveWhileFiring?: number;
  /** Tir sans gâchette (remplacement automatique des drones/mines) ; ne lève pas la protection. */
  alwaysFire?: boolean;
}

export interface BodyProfile {
  speed: number;
  health: number;
  bodyDamage: number;
  /** Élan : bonus de vitesse et de dégâts corporels après un trajet droit, perdu en virage. */
  momentum?: { speed: number; damage: number; build: number };
  /** Blindage directionnel : réduction des dégâts reçus de face, majoration de l'arrière. */
  frontArmor?: { reduction: number; rearMul: number };
  /** Multiplicateur de l'intervalle de contact (impact fort puis délai). */
  contactInterval?: number;
}

export const DEFAULT_BODY: BodyProfile = { speed: 1, health: 1, bodyDamage: 1 };

/** Plafonds de sécurité des effets (dossier §11 : caps, pas d'invulnérabilité ni d'immobilisation totale). */
export const EFFECT_CAPS = { minSlowMul: 0.55, maxKnock: 420, maxPull: 260, maxChainJumps: 4, maxFrags: 12, maxActive: 24 };

/** Valeurs de base d'un projectile, multipliées par le tube, la classe et les stats. */
export const BULLET_BASE = {
  damage: 10,
  pen: 12,
  speed: 520,
  life: 1.6,
  recoil: 14,
};

export const single = (o: Partial<BarrelDef> = {}): BarrelDef => ({
  angle: 0,
  offset: 0,
  length: 1.9,
  width: 0.85,
  delay: 0,
  ...o,
});

export interface ClassKit {
  weapon: WeaponDef;
  body?: Partial<BodyProfile>;
}

export const KITS: Record<string, ClassKit> = {
  BASE: { weapon: { reload: 0.6, barrels: [single()] } },

  // ── C01 Assaut ─────────────────────────────────────────────
  'C01-F': { weapon: { reload: 0.5, barrels: [single({ length: 2.0, width: 0.95, damage: 1.1 })] } },
  'C01-A': {
    weapon: {
      reload: 0.5,
      barrels: [
        single({ offset: -0.42, width: 0.72, damage: 0.75, spread: 4 }),
        single({ offset: 0.42, width: 0.72, damage: 0.75, spread: 4, delay: 0.5 }),
      ],
    },
  },
  'C01-B': {
    weapon: {
      reload: 0.95,
      burst: { count: 3, interval: 0.08 },
      barrels: [single({ length: 2.05, width: 0.9, damage: 0.85, spread: 2 })],
    },
  },
  'C01-C': {
    weapon: {
      reload: 0.55,
      ramp: { max: 2.2, perShot: 0.08, decay: 1.2 },
      heat: { perShot: 0.035, cool: 0.32, lockout: 0.35 },
      barrels: [single({ length: 2.1, width: 0.8, damage: 0.62, spread: 3 })],
    },
  },
  'C01-A1': {
    weapon: {
      reload: 0.5,
      barrels: [
        single({ offset: -0.66, width: 0.5, length: 1.75, damage: 0.6, spread: 8, delay: 0 }),
        single({ offset: -0.22, width: 0.5, length: 1.95, damage: 0.6, spread: 8, delay: 0.5 }),
        single({ offset: 0.22, width: 0.5, length: 1.95, damage: 0.6, spread: 8, delay: 0.25 }),
        single({ offset: 0.66, width: 0.5, length: 1.75, damage: 0.6, spread: 8, delay: 0.75 }),
      ],
    },
  },
  'C01-A2': {
    weapon: {
      reload: 0.5,
      barrels: [
        single({ angle: -28, offset: -0.18, width: 0.55, damage: 0.72, spread: 3 }),
        single({ angle: -28, offset: 0.18, width: 0.55, damage: 0.72, spread: 3, delay: 0.5 }),
        single({ angle: 28, offset: -0.18, width: 0.55, damage: 0.72, spread: 3, delay: 0.25 }),
        single({ angle: 28, offset: 0.18, width: 0.55, damage: 0.72, spread: 3, delay: 0.75 }),
      ],
    },
  },
  'C01-B1': {
    weapon: {
      reload: 1.05,
      burst: { count: 4, interval: 0.045 },
      barrels: [single({ length: 2.1, width: 1.0, damage: 0.95, spread: 1.5, recoil: 3.2 })],
    },
  },
  'C01-B2': {
    weapon: {
      reload: 2.6,
      burst: { count: 9, interval: 0.07 },
      barrels: [single({ length: 2.15, width: 0.9, damage: 0.8, spread: 3 })],
    },
  },
  'C01-C1': {
    weapon: {
      reload: 0.5,
      ramp: { max: 3.1, perShot: 0.07, decay: 1.4 },
      heat: { perShot: 0.03, cool: 0.3, lockout: 0.3 },
      barrels: [single({ length: 2.25, width: 0.78, damage: 0.48, spread: 4.5, life: 0.55 })],
    },
  },
  'C01-C2': {
    weapon: {
      reload: 0.7,
      spin: 110,
      barrels: [0, 60, 120, 180, 240, 300].map((a, i) =>
        single({ angle: a, length: 1.7, width: 0.62, damage: 0.55, spread: 6, delay: i / 6, recoil: 0.3 }),
      ),
    },
    body: { speed: 0.95 },
  },

  // ── C10 Dispersion ─────────────────────────────────────────
  'C10-F': {
    weapon: {
      reload: 0.9,
      barrels: [single({ width: 0.9, flare: 0.35, pellets: 5, spread: 14, damage: 0.45, life: 0.5, speed: 0.95, recoil: 2 })],
    },
  },
  'C10-A': {
    weapon: {
      reload: 0.95,
      barrels: [single({ width: 1.0, flare: 0.6, pellets: 9, spread: 26, damage: 0.3, life: 0.5, speed: 0.9, recoil: 2.2 })],
    },
  },
  'C10-B': {
    weapon: {
      reload: 1.45,
      barrels: [
        single({ offset: -0.38, width: 0.7, flare: 0.25, pellets: 5, spread: 14, damage: 0.42, life: 0.5 }),
        single({ offset: 0.38, width: 0.7, flare: 0.25, pellets: 5, spread: 14, damage: 0.42, life: 0.5, delay: 0.14 }),
      ],
    },
  },
  'C10-C': {
    weapon: {
      reload: 0.9,
      focus: { time: 1.2, minSpread: 0.35 },
      moveWhileFiring: 0.78,
      barrels: [single({ width: 0.9, flare: 0.3, pellets: 6, spread: 16, damage: 0.45, life: 0.55 })],
    },
  },
  'C10-A1': {
    weapon: {
      reload: 1.05,
      barrels: [single({ width: 1.1, flare: 0.8, pellets: 14, spread: 38, damage: 0.26, life: 0.5, speed: 0.9, recoil: 2.4 })],
    },
  },
  'C10-A2': {
    weapon: {
      reload: 0.85,
      barrels: [
        single({ angle: -70, width: 0.8, flare: 0.5, pellets: 6, spread: 20, damage: 0.4, life: 0.34 }),
        single({ angle: 70, width: 0.8, flare: 0.5, pellets: 6, spread: 20, damage: 0.4, life: 0.34, delay: 0.5 }),
        single({ width: 0.55, length: 1.4, damage: 0.4, life: 0.4 }),
      ],
    },
  },
  'C10-B1': {
    weapon: {
      reload: 1.9,
      burst: { count: 2, interval: 0.12 },
      barrels: [single({ width: 0.95, flare: 0.35, pellets: 6, spread: 15, damage: 0.45, life: 0.5, recoil: 2 })],
    },
  },
  'C10-B2': {
    weapon: {
      reload: 0.75,
      barrels: [
        single({ angle: -14, offset: -0.3, width: 0.75, flare: 0.3, pellets: 5, spread: 12, damage: 0.4, life: 0.5 }),
        single({ angle: 14, offset: 0.3, width: 0.75, flare: 0.3, pellets: 5, spread: 12, damage: 0.4, life: 0.5, delay: 0.5 }),
      ],
    },
  },
  'C10-C1': {
    weapon: {
      reload: 0.6,
      charge: { time: 1.4, minFrac: 0.15, damageAt0: 0.6, damageAt1: 1.5, moveMul: 0.7 },
      focus: { time: 1.4, minSpread: 0.25 },
      barrels: [single({ width: 1.0, length: 2.1, flare: 0.25, pellets: 8, spread: 18, damage: 0.45, life: 0.6 })],
    },
  },
  'C10-C2': {
    weapon: {
      reload: 1.0,
      barrels: [single({ width: 1.05, flare: 0.45, pellets: 10, spread: 22, damage: 0.32, pen: 0.5, life: 1.1, speed: 0.55, size: 0.8 })],
    },
  },
};
